import { useCallback, useEffect, useRef, useState } from 'react'
import type { ConsultantProfile } from '../api'
import {
  consultantPhotoUrl,
  deleteConsultantPhoto,
  getConsultantProfile,
  updateConsultantProfile,
  uploadConsultantPhoto,
} from '../api'
import { applyPalette, loadPaletteId, PALETTES, type PaletteId } from '../palettes'
import { clearStoredSiteKey, getStoredSiteKey } from '../siteAuth'
import { BrandMark } from './BrandMark'

type CacheState = {
  state: 'none' | 'seed' | 'verified'
  parser_model?: string | null
  confidence?: string | null
  parsed_at?: string | null
  version_date?: string | null
}

type RecentJob = {
  id: string
  kind: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  error: string | null
  started_at: string | null
  completed_at: string | null
}

type LgaStatus = {
  slug: string
  name: string
  lep_name: string | null
  has_legislation_url: boolean
  lep_cache: CacheState
  dcp_cache: CacheState
  dcp_sources_count: number
  recent_jobs: RecentJob[]
}

// Persistent background-job tracker. Every upload's job_id lands in
// localStorage as soon as the multipart POST returns and stays there
// until the parse hits a terminal state — closing the tab or hitting
// reload doesn't lose track of the work.
interface TrackedJob {
  jobId: string
  slug: string
  kind: 'lep' | 'dcp'
  filesLabel: string
  sizeLabel: string
  startedAt: number   // epoch ms
}

const TRACKED_JOBS_KEY = 'massing_admin_active_jobs_v1'

function loadTrackedJobs(): TrackedJob[] {
  try {
    const raw = localStorage.getItem(TRACKED_JOBS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Drop anything older than 30 min — a stale tracker probably means
    // the server crashed or the job_id has since been evicted, and
    // polling forever would peg the admin tab.
    const cutoff = Date.now() - 30 * 60 * 1000
    return parsed.filter(
      (j): j is TrackedJob =>
        j && typeof j.jobId === 'string' && typeof j.startedAt === 'number' && j.startedAt > cutoff,
    )
  } catch {
    return []
  }
}

function saveTrackedJobs(jobs: TrackedJob[]): void {
  try {
    if (jobs.length === 0) localStorage.removeItem(TRACKED_JOBS_KEY)
    else localStorage.setItem(TRACKED_JOBS_KEY, JSON.stringify(jobs))
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/**
 * Admin entry point. By the time this renders the visitor has already
 * cleared the site-wide <SiteGate>, so the shared password (== the
 * admin key) is in localStorage. We read it straight from there and
 * use it for the admin API calls — no separate admin login, no
 * `?key=…` in the URL. The non-null assertion is safe: /admin is
 * always wrapped by SiteGate, which guarantees a stored key.
 */
export function AdminPage() {
  const adminKey = getStoredSiteKey() ?? ''
  return (
    <AdminDashboard
      adminKey={adminKey}
      onLogout={() => {
        clearStoredSiteKey()
        // Bounce back to the site gate.
        window.location.reload()
      }}
    />
  )
}


function AdminDashboard({ adminKey, onLogout }: { adminKey: string; onLogout: () => void }) {
  const [rows, setRows] = useState<LgaStatus[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  const [toast, setToast] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  // Background-job tracker — every active upload's job_id is
  // persisted to localStorage the moment the multipart POST returns,
  // so closing the tab or refreshing doesn't lose the parse. On
  // mount we hydrate any in-flight jobs and resume polling them
  // automatically; the server has already accepted the file, the
  // parse runs as an `asyncio.create_task`, and the only thing
  // missing was the UI's awareness of "this is still happening".
  const [activeJobs, setActiveJobs] = useState<TrackedJob[]>(() => loadTrackedJobs())
  // Tick once a second so each tracker re-renders its elapsed time.
  const [tickNow, setTickNow] = useState<number>(() => Date.now())
  // Jobs we already have a poll loop running for. Ref-based so
  // re-renders don't spawn duplicate polls.
  const polledRef = useRef<Set<string>>(new Set())

  // Sync trackedJobs to localStorage every time the list changes,
  // so a refresh between renders never loses the active set.
  useEffect(() => {
    saveTrackedJobs(activeJobs)
  }, [activeJobs])

  // Cheap 1-s tick so each tracker row updates its elapsed counter.
  // Stops when there are no active jobs to display.
  useEffect(() => {
    if (activeJobs.length === 0) return
    const t = window.setInterval(() => setTickNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [activeJobs.length])

  // For each tracked job, spawn a poll loop once (and only once —
  // polledRef de-dupes across re-renders + remounts of this effect).
  // The loop survives until the job hits a terminal state, the
  // job_id 404s, or the user dismisses the tracker.
  useEffect(() => {
    if (!adminKey) return
    activeJobs.forEach((j) => {
      if (polledRef.current.has(j.jobId)) return
      polledRef.current.add(j.jobId)
      void pollTrackedJob(j)
    })
  })

  async function pollTrackedJob(j: TrackedJob): Promise<void> {
    while (polledRef.current.has(j.jobId)) {
      await new Promise((r) => setTimeout(r, 3000))
      if (!polledRef.current.has(j.jobId)) return
      let jb: { status?: string; error?: string | null; result?: Record<string, unknown> } | null = null
      try {
        const res = await fetch(`/api/admin/jobs/${j.jobId}?key=${encodeURIComponent(adminKey)}`)
        if (res.status === 404) {
          // Job evicted server-side — drop the tracker silently.
          polledRef.current.delete(j.jobId)
          setActiveJobs((prev) => prev.filter((x) => x.jobId !== j.jobId))
          return
        }
        if (!res.ok) {
          // Network blip / proxy hiccup — keep polling.
          continue
        }
        jb = await res.json()
      } catch {
        continue
      }
      if (!jb) continue
      if (jb.status === 'succeeded') {
        polledRef.current.delete(j.jobId)
        const elapsed = Math.round((Date.now() - j.startedAt) / 1000)
        setToast(
          `${j.kind.toUpperCase()} parsed for ${j.slug.toUpperCase()} in ${elapsed}s — ${j.filesLabel}.`,
        )
        setActiveJobs((prev) => prev.filter((x) => x.jobId !== j.jobId))
        load()
        return
      }
      if (jb.status === 'failed') {
        polledRef.current.delete(j.jobId)
        const elapsed = Math.round((Date.now() - j.startedAt) / 1000)
        setError(
          `${j.kind.toUpperCase()} parse for ${j.slug.toUpperCase()} failed after ${elapsed}s: ${jb.error || 'unknown error'}`,
        )
        setActiveJobs((prev) => prev.filter((x) => x.jobId !== j.jobId))
        load()
        return
      }
      // pending / running — keep looping; the tickNow effect re-paints
      // elapsed counters on the tracker rows.
    }
  }

  const load = useCallback(async () => {
    if (!adminKey) {
      setError('Add ?key=<ADMIN_API_KEY> to the URL to load this page.')
      return
    }
    try {
      const r = await fetch(`/api/admin/lga-status?key=${encodeURIComponent(adminKey)}`)
      if (!r.ok) {
        setError(`Failed to load status: HTTP ${r.status}`)
        return
      }
      setRows(await r.json())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [adminKey])

  // Initial load
  useEffect(() => { load() }, [load])

  // Auto-refresh while any LGA has a running/pending job
  useEffect(() => {
    if (!rows) return
    const hasActive = rows.some((r) =>
      r.recent_jobs.some((j) => j.status === 'pending' || j.status === 'running')
    )
    if (!hasActive) return
    const t = setInterval(load, 5000)
    return () => clearInterval(t)
  }, [rows, load])

  async function triggerParse(slug: string, kind: 'lep' | 'dcp') {
    setBusy((b) => ({ ...b, [`${kind}:${slug}`]: true }))
    setToast(`Started ${kind.toUpperCase()} parse for ${slug} — typically takes 30–90 s. Auto-refreshing…`)
    try {
      const r = await fetch(
        `/api/admin/parse-${kind}?lga=${slug}&key=${encodeURIComponent(adminKey)}`,
        { method: 'POST' }
      )
      if (!r.ok) {
        const body = await r.text().catch(() => '')
        setError(`parse-${kind} failed to schedule: ${body || `HTTP ${r.status}`}`)
        setToast(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setToast(null)
    } finally {
      setTimeout(() => {
        setBusy((b) => ({ ...b, [`${kind}:${slug}`]: false }))
        load()
      }, 2000)
    }
  }

  async function uploadFile(slug: string, kind: 'lep' | 'dcp', files: File[]) {
    const busyKey = `upload-${kind}:${slug}`
    setBusy((b) => ({ ...b, [busyKey]: true }))
    const totalBytes = files.reduce((n, f) => n + f.size, 0)
    const sizeMb = totalBytes / (1024 * 1024)
    const sizeLabel = sizeMb >= 1 ? `${sizeMb.toFixed(1)} MB` : `${(totalBytes / 1024).toFixed(0)} kB`
    const oversize = sizeMb > 40
    const filesLabel =
      files.length === 1
        ? files[0].name
        : `${files.length} files (${files.map((f) => f.name.replace(/\.[^.]+$/, '')).slice(0, 3).join(', ')}${files.length > 3 ? `, +${files.length - 3} more` : ''})`
    setError(null)
    setToast(
      `Uploading ${filesLabel} (${sizeLabel}) → ${slug.toUpperCase()} ${kind.toUpperCase()}. ` +
      (oversize
        ? 'Large upload — review may take several minutes.'
        : 'Upload + review usually takes 30–120 s. The button stays locked until the review finishes.')
    )
    const startedAt = Date.now()
    try {
      const fd = new FormData()
      // DCP accepts multiple PDFs; LEP is single. Either way we send
      // every file under the same field name the server expects.
      const fieldName = kind === 'dcp' ? 'files' : 'file'
      files.forEach((f) => fd.append(fieldName, f))
      const r = await fetch(
        `/api/admin/upload-${kind}?lga=${slug}&key=${encodeURIComponent(adminKey)}`,
        { method: 'POST', body: fd }
      )
      const rawBody = await r.text()
      if (!r.ok) {
        // Servers send JSON with a `detail` field on validation errors.
        // Plain-text fallback covers nginx / Railway proxy bodies.
        let detail = rawBody
        try {
          const parsed = JSON.parse(rawBody)
          if (parsed && typeof parsed === 'object' && parsed.detail) {
            detail = typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail)
          }
        } catch { /* not JSON */ }
        setError(`upload-${kind} failed (${r.status}): ${detail || `HTTP ${r.status}`}`)
        setToast(null)
        setBusy((b) => ({ ...b, [busyKey]: false }))
        return
      }
      // 202-style response: { job_id, kind, status: 'pending', check }
      let job: { job_id?: string; status?: string; error?: string; result?: Record<string, unknown> } = {}
      try { job = JSON.parse(rawBody) } catch { /* unparseable */ }
      const jobId = job.job_id
      if (!jobId) {
        setToast(`Upload accepted — no job ID returned. Refreshing.`)
        setBusy((b) => ({ ...b, [busyKey]: false }))
        load()
        return
      }
      // Register the job for background tracking. The poll loop in
      // pollTrackedJob picks it up on the next tick, and the same
      // entry survives in localStorage across tab close / refresh.
      setToast(
        `Upload accepted (${sizeLabel}). Review continues in the background — safe to close this tab.`,
      )
      setActiveJobs((prev) => [
        ...prev.filter((p) => p.jobId !== jobId),
        { jobId, slug, kind, filesLabel, sizeLabel, startedAt },
      ])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setToast(null)
    } finally {
      setBusy((b) => ({ ...b, [busyKey]: false }))
      load()
    }
  }

  return (
    <div className="min-h-full">
      <header
        className="sticky top-0 z-10 backdrop-blur-md header-bg border-b border-massing-rule"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto px-8 h-[72px] pt-3 pb-3 flex items-end justify-between">
          <a href="/" className="flex items-center gap-3 no-underline">
            <BrandMark size={36} />
            <span className="brand-wordmark text-3xl brand-gradient-text leading-none">massing</span>
            <span className="inline-block w-px h-5 bg-massing-rule" aria-hidden />
            <span className="brand-eyebrow text-massing-ink-light">admin · LEP/DCP coverage</span>
          </a>
          <div className="flex items-center gap-4">
            <button
              onClick={load}
              className="brand-eyebrow text-massing hover:underline"
            >
              Refresh
            </button>
            <button
              onClick={onLogout}
              className="brand-eyebrow text-massing-ink-light hover:text-massing-ink"
            >
              Lock
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-8 py-10">
        <PalettePicker />
        <ConsultantProfileEditor adminKey={adminKey} />

        {/* Active background-job tracker. Every upload's job survives
            here across tab close / refresh — pollTrackedJob runs
            forever until the server stamps succeeded / failed. */}
        {activeJobs.length > 0 && (
          <div className="mb-6 rounded-md border border-massing-highlight/30 glass overflow-hidden">
            <div className="px-4 py-2.5 border-b border-massing-rule-soft flex items-center justify-between">
              <span className="brand-eyebrow text-massing-ink-light">
                {activeJobs.length === 1
                  ? 'Background review in progress'
                  : `${activeJobs.length} background reviews in progress`}
              </span>
              <span className="text-[11px] text-massing-ink-light italic">
                safe to close this tab — review continues on the server
              </span>
            </div>
            <ul className="divide-y divide-massing-rule-soft">
              {activeJobs.map((j) => {
                const elapsed = Math.round((tickNow - j.startedAt) / 1000)
                const mm = Math.floor(elapsed / 60).toString().padStart(2, '0')
                const ss = (elapsed % 60).toString().padStart(2, '0')
                return (
                  <li key={j.jobId} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-flag-amber animate-pulse" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="text-massing-ink">
                        <span className="font-medium uppercase tracking-wide">{j.slug}</span>
                        <span className="text-massing-ink-light"> · {j.kind.toUpperCase()} review</span>
                      </div>
                      <div className="text-[12px] text-massing-ink-mid truncate">
                        {j.filesLabel} ({j.sizeLabel})
                      </div>
                    </div>
                    <div className="font-mono text-[14px] tabular-nums text-massing-ink">
                      {mm}:{ss}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        polledRef.current.delete(j.jobId)
                        setActiveJobs((prev) => prev.filter((x) => x.jobId !== j.jobId))
                      }}
                      className="text-[11px] text-massing-ink-light hover:text-massing-maroon"
                      title="Stop tracking this job in this browser (the server-side review keeps running)"
                    >
                      dismiss
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {toast && (
          <div className="mb-4 p-3 bg-massing-cream border border-massing-rule text-massing-ink text-sm flex items-start justify-between gap-3">
            <span>{toast}</span>
            <button onClick={() => setToast(null)} className="text-massing-ink-light hover:text-massing-ink">dismiss</button>
          </div>
        )}
        {error && (
          <div className="mb-6 p-3 bg-massing-maroon-soft border border-massing-rule text-massing-maroon rounded-sm text-sm flex items-start justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-massing-maroon hover:underline">dismiss</button>
          </div>
        )}
        {!rows && !error && <p className="text-massing-ink-mid">Loading…</p>}
        {rows && (
          <>
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              {/* The registry carries 37 LGAs in source order — without a
                  filter the operator has to scroll the whole list to find
                  one council. The input matches against both the slug
                  and the display name, case-insensitive. */}
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Find an LGA — e.g. strathfield, ku-ring-gai, sutherland…"
                className="flex-1 min-w-[280px] max-w-md border border-massing-rule bg-massing-paper-deep px-3 py-2 text-sm text-massing-ink placeholder:text-massing-ink-light/70 focus:outline-none focus:border-massing-highlight rounded-md"
                autoFocus
              />
              <span className="brand-eyebrow text-massing-ink-light">
                {(() => {
                  const total = rows.length
                  const f = filter.trim().toLowerCase()
                  const shown = f
                    ? rows.filter((r) => r.slug.toLowerCase().includes(f) || r.name.toLowerCase().includes(f)).length
                    : total
                  return f ? `${shown} of ${total} LGAs` : `${total} LGAs`
                })()}
              </span>
            </div>
            <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-massing-rule">
                <th className="text-left brand-eyebrow pb-2 pr-3">LGA</th>
                <th className="text-left brand-eyebrow pb-2 pr-3">LEP cache</th>
                <th className="text-left brand-eyebrow pb-2 pr-3">DCP cache</th>
                <th className="text-left brand-eyebrow pb-2 pr-3">DCP source URLs</th>
                <th className="text-left brand-eyebrow pb-2 pr-3">Last activity</th>
                <th className="text-left brand-eyebrow pb-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Alphabetise + filter so the operator can scan the list
                // by council name without scrolling the source order.
                const f = filter.trim().toLowerCase()
                const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name))
                const visible = f
                  ? sorted.filter((r) =>
                      r.slug.toLowerCase().includes(f) || r.name.toLowerCase().includes(f),
                    )
                  : sorted
                return visible
              })().map((r) => (
                <Row
                  key={r.slug}
                  r={r}
                  adminKey={adminKey}
                  busy={busy}
                  onParse={triggerParse}
                  onUpload={uploadFile}
                />
              ))}
            </tbody>
          </table>
          </>
        )}

      </main>
    </div>
  )
}


function Row({
  r,
  adminKey: _adminKey,
  busy,
  onParse,
  onUpload,
}: {
  r: LgaStatus
  adminKey: string
  busy: Record<string, boolean>
  onParse: (slug: string, kind: 'lep' | 'dcp') => void
  onUpload: (slug: string, kind: 'lep' | 'dcp', files: File[]) => void
}) {
  const lepJob = r.recent_jobs.find((j) => j.kind === 'parse_lep')
  const dcpJob = r.recent_jobs.find((j) => j.kind === 'parse_dcp')
  const lepRunning = lepJob && (lepJob.status === 'pending' || lepJob.status === 'running')
  const dcpRunning = dcpJob && (dcpJob.status === 'pending' || dcpJob.status === 'running')
  const lastActivity = r.recent_jobs[0]
  return (
    <tr className="border-b border-massing-rule-soft align-top">
      <td className="py-3 pr-3">
        <div className="font-medium">{r.name}</div>
        <div className="text-[11px] text-massing-ink-light">{r.slug}</div>
      </td>
      <td className="py-3 pr-3">
        <CacheBadge state={r.lep_cache} />
        {lepJob && lepJob.status === 'failed' && (
          <div className="text-[11px] text-massing-maroon mt-1 italic max-w-[280px] truncate" title={lepJob.error ?? ''}>
            last parse failed: {lepJob.error?.split('\n')[0]}
          </div>
        )}
        {!r.has_legislation_url && (
          <div className="text-[11px] text-massing-ink-light mt-1 italic">
            no legislation URL in registry
          </div>
        )}
      </td>
      <td className="py-3 pr-3">
        <CacheBadge state={r.dcp_cache} />
        {dcpJob && dcpJob.status === 'failed' && (
          <div className="text-[11px] text-massing-maroon mt-1 italic max-w-[280px] truncate" title={dcpJob.error ?? ''}>
            last parse failed: {dcpJob.error?.split('\n')[0]}
          </div>
        )}
      </td>
      <td className="py-3 pr-3">
        {r.dcp_sources_count > 0 ? (
          <span>{r.dcp_sources_count} URL{r.dcp_sources_count === 1 ? '' : 's'}</span>
        ) : (
          <span className="text-massing-ink-light">none</span>
        )}
      </td>
      <td className="py-3 pr-3 text-[11px] text-massing-ink-mid">
        {lastActivity ? (
          <>
            <div className="capitalize">{lastActivity.kind.replace('_', ' ')} · {lastActivity.status}</div>
            <div className="text-massing-ink-light">
              {lastActivity.completed_at?.replace('T', ' ').slice(0, 16) ??
                lastActivity.started_at?.replace('T', ' ').slice(0, 16) ??
                ''}
            </div>
          </>
        ) : (
          <span className="text-massing-ink-light">—</span>
        )}
      </td>
      <td className="py-3 pr-3">
        <div className="flex flex-col gap-1.5 min-w-[170px]">
          <div className="flex gap-1">
            <button
              onClick={() => onParse(r.slug, 'lep')}
              disabled={!r.has_legislation_url || lepRunning || busy[`lep:${r.slug}`]}
              className="text-xs px-2 py-1 border border-massing-rule rounded-sm hover:bg-massing-cream disabled:opacity-40 disabled:cursor-not-allowed flex-1"
              title={!r.has_legislation_url ? 'No legislation URL in registry' : ''}
            >
              {lepRunning ? 'Parsing LEP…' : 'Parse LEP'}
            </button>
            <UploadButton
              kind="lep"
              slug={r.slug}
              busy={busy[`upload-lep:${r.slug}`]}
              onUpload={(files) => onUpload(r.slug, 'lep', files)}
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onParse(r.slug, 'dcp')}
              disabled={r.dcp_sources_count === 0 || dcpRunning || busy[`dcp:${r.slug}`]}
              className="text-xs px-2 py-1 border border-massing-rule rounded-sm hover:bg-massing-cream disabled:opacity-40 disabled:cursor-not-allowed flex-1"
              title={r.dcp_sources_count === 0 ? 'No DCP source URLs registered' : ''}
            >
              {dcpRunning ? 'Parsing DCP…' : 'Parse DCP'}
            </button>
            <UploadButton
              kind="dcp"
              slug={r.slug}
              busy={busy[`upload-dcp:${r.slug}`]}
              onUpload={(files) => onUpload(r.slug, 'dcp', files)}
            />
          </div>
        </div>
      </td>
    </tr>
  )
}

function UploadButton({
  kind,
  slug,
  busy,
  onUpload,
}: {
  kind: 'lep' | 'dcp'
  slug: string
  busy: boolean
  onUpload: (files: File[]) => void
}) {
  const inputId = `upload-${kind}-${slug}`
  // DCPs are routinely split across 10–20 part-PDFs on council
  // websites (Part A dwelling houses, Part B dual occ, etc.). Allow
  // multi-select on DCP so the operator submits every relevant part
  // in one request and the parser sees the whole DCP together.
  const allowMultiple = kind === 'dcp'
  return (
    <>
      <label
        htmlFor={inputId}
        title={
          allowMultiple
            ? `Upload one or more ${kind.toUpperCase()} part-PDFs — pick every residential-relevant chapter together`
            : `Upload ${kind.toUpperCase()} PDF / HTML / text`
        }
        className={
          'text-xs px-2 py-1 border border-massing-rule rounded-sm hover:bg-massing-cream cursor-pointer ' +
          (busy ? 'opacity-50 pointer-events-none' : '')
        }
      >
        {busy ? 'Uploading…' : (allowMultiple ? 'Upload parts' : 'Upload')}
      </label>
      <input
        id={inputId}
        type="file"
        accept=".pdf,.html,.htm,.txt"
        multiple={allowMultiple}
        className="hidden"
        onChange={(e) => {
          const list = e.target.files
          if (list && list.length > 0) onUpload(Array.from(list))
          // Reset so re-selecting the same file(s) fires onChange again.
          e.target.value = ''
        }}
      />
    </>
  )
}

// --------------------------------------------------------------------
// Consultant profile editor — singleton row, edits the "Prepared by"
// block rendered on every report's cover + end pages. Photo upload
// goes through a separate multipart endpoint; everything else is a
// single PUT of the JSON shape.
// --------------------------------------------------------------------

function ConsultantProfileEditor({ adminKey }: { adminKey: string }) {
  const [profile, setProfile] = useState<ConsultantProfile | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  // Bumped after each photo upload so the <img> URL changes, forcing
  // the browser to refetch (the photo endpoint always returns the
  // current photo so there's no per-photo URL to point at).
  const [photoVer, setPhotoVer] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    getConsultantProfile()
      .then((p) => {
        setProfile(p)
        setDraft({
          full_name: p.full_name ?? '',
          title: p.title ?? '',
          registration_no: p.registration_no ?? '',
          email: p.email ?? '',
          phone: p.phone ?? '',
          website: p.website ?? '',
        })
      })
      .catch((e) => setError(String(e)))
  }, [])

  async function onSave() {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const next = await updateConsultantProfile(
        {
          full_name: draft.full_name || null,
          title: draft.title || null,
          registration_no: draft.registration_no || null,
          email: draft.email || null,
          phone: draft.phone || null,
          website: draft.website || null,
        },
        adminKey,
      )
      setProfile(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  async function onPhoto(file: File) {
    setUploading(true)
    setError(null)
    try {
      const next = await uploadConsultantPhoto(file, adminKey)
      setProfile(next)
      setPhotoVer((v) => v + 1)
    } catch (e) {
      setError(String(e))
    } finally {
      setUploading(false)
    }
  }

  async function onDeletePhoto() {
    setUploading(true)
    setError(null)
    try {
      const next = await deleteConsultantPhoto(adminKey)
      setProfile(next)
      setPhotoVer((v) => v + 1)
    } catch (e) {
      setError(String(e))
    } finally {
      setUploading(false)
    }
  }

  if (!profile) {
    return (
      <section className="mb-6 rounded-md border border-massing-rule-soft p-5 text-sm text-massing-ink-light">
        Loading consultant profile…
      </section>
    )
  }

  return (
    <section className="mb-6 rounded-md border border-massing-rule overflow-hidden">
      <div className="px-5 py-3 border-b border-massing-rule-soft flex items-baseline justify-between">
        <div>
          <span className="brand-eyebrow text-massing-ink-light">
            Prepared by · cover + end of every report
          </span>
        </div>
        {saved && (
          <span className="text-[11.5px] text-flag-green">Saved.</span>
        )}
      </div>
      <div className="p-5 flex gap-6 items-start">
        {/* Photo column */}
        <div className="shrink-0 w-32">
          <div className="w-32 h-32 rounded-full overflow-hidden border border-massing-rule bg-massing-cream/40 flex items-center justify-center">
            {profile.has_photo ? (
              <img
                src={`${consultantPhotoUrl()}?v=${photoVer}`}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[11px] text-massing-ink-light text-center px-2">
                No photo
              </span>
            )}
          </div>
          <div className="mt-3 flex flex-col gap-1.5">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) onPhoto(f)
                e.target.value = ''
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="text-[11.5px] bg-massing hover:bg-massing-deep text-massing-cream px-3 py-1.5 rounded-sm disabled:opacity-60"
            >
              {uploading ? 'Uploading…' : profile.has_photo ? 'Replace photo' : 'Upload photo'}
            </button>
            {profile.has_photo && (
              <button
                type="button"
                onClick={onDeletePhoto}
                disabled={uploading}
                className="text-[11px] text-massing-ink-light hover:text-flag-red"
              >
                Remove
              </button>
            )}
            <p className="text-[10px] text-massing-ink-light leading-snug mt-1">
              JPEG / PNG / WebP · 2 MB max · square 400×400 ideal
            </p>
          </div>
        </div>

        {/* Fields column */}
        <div className="flex-1 grid grid-cols-2 gap-x-5 gap-y-3">
          <Field label="Full name" value={draft.full_name} onChange={(v) => setDraft({ ...draft, full_name: v })} />
          <Field label="Title / role" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
          <Field label="Registration / Reg. no." value={draft.registration_no} onChange={(v) => setDraft({ ...draft, registration_no: v })} />
          <Field label="Email" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} type="email" />
          <Field label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} />
          <Field label="Website" value={draft.website} onChange={(v) => setDraft({ ...draft, website: v })} />
          <div className="col-span-2 flex items-center justify-end gap-3 mt-2">
            {error && (
              <span className="text-[11.5px] text-flag-red mr-auto">
                {error}
              </span>
            )}
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="text-sm bg-massing hover:bg-massing-deep text-massing-cream px-4 py-1.5 rounded-sm disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="brand-eyebrow text-massing-ink-light">{label}</span>
      {/* Match the LGA filter input's treatment so dark-theme palettes
          (Aurora, Sunset, Forest, Ember) render readable text against
          a clearly-typed input surface. Earlier `bg-white` + no text
          colour inherited the page's light text on a white input,
          making typed values nearly invisible on dark palettes. */}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-massing-rule bg-massing-paper-deep text-massing-ink placeholder:text-massing-ink-light/70 rounded-sm px-2.5 py-1.5 text-sm focus:border-massing-highlight focus:outline-none"
      />
    </label>
  )
}

function PalettePicker() {
  // The user-facing app, the report templates, the admin header all
  // pull from the same CSS custom properties — switching here
  // re-paints the entire identity in one shot. localStorage keeps
  // the choice across reloads.
  const [paletteId, setPaletteId] = useState<PaletteId>(() => loadPaletteId())
  const current = PALETTES.find((p) => p.id === paletteId) ?? PALETTES[0]
  return (
    <div className="mb-6 p-4 rounded-md glass">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="brand-eyebrow mb-1.5">Brand palette (temporary admin control)</div>
          <p className="text-[12px] text-massing-ink-light max-w-xl">
            Switches every gradient, the aurora background, the wordmark
            and the accent rings to the chosen palette. Saved to this
            browser only — pick a winner and we'll wire the final
            choice into the source.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={paletteId}
            onChange={(e) => {
              const id = e.target.value as PaletteId
              setPaletteId(id)
              applyPalette(id)
            }}
            className="border border-massing-rule bg-massing-paper-deep px-3 py-2 text-massing-ink focus:outline-none focus:border-massing-highlight rounded-md text-sm"
          >
            {PALETTES.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <div className="flex items-center gap-1.5">
            {/* Five swatches — exactly the gradient-bg stops, so the
                preview reflects the strip you'll see on dots / buttons. */}
            {['--grad-bg-1', '--grad-bg-2', '--grad-bg-3', '--grad-bg-4', '--grad-bg-5'].map((k) => (
              <span
                key={k}
                className="inline-block w-4 h-4 rounded-full border border-massing-rule"
                style={{ background: current.vars[k] }}
                aria-hidden
              />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-2 text-[11.5px] text-massing-ink-mid italic">
        {current.description}
      </div>
    </div>
  )
}

function CacheBadge({ state }: { state: CacheState }) {
  if (state.state === 'none') {
    return <span className="text-[11px] uppercase tracking-wide bg-massing-rule-soft text-massing-ink-light px-1.5 py-0.5 rounded">not parsed</span>
  }
  if (state.state === 'seed') {
    return (
      <span className="text-[11px] uppercase tracking-wide bg-massing-cream text-massing-ink-mid px-1.5 py-0.5 rounded">
        manual seed
      </span>
    )
  }
  return (
    <div>
      <span className="text-[11px] uppercase tracking-wide bg-massing text-white px-1.5 py-0.5 rounded">
        Verified
      </span>
      {state.parsed_at && (
        <span className="text-[11px] text-massing-ink-light ml-2">
          {state.parsed_at.replace('T', ' ').slice(0, 16)}
        </span>
      )}
      {state.confidence && (
        <span className="text-[11px] text-massing-ink-light ml-2">· {state.confidence}</span>
      )}
    </div>
  )
}
